import React, { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import { connect } from "react-redux";
import { DebounceInput } from "react-debounce-input";
import { createColumnHelper } from "@tanstack/react-table";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowsRotate } from "@fortawesome/free-solid-svg-icons";

import {
  emptyString,
  accessTypesEnums,
  userPermissionsConstants,
  accessTypeFilterDropDown,
  brandConfigurationConstants,
  brandConfigurationLabels,
  BUTTON_TEXT,
} from "../../constants";
import {
  useSpinner,
  Spinner,
  DataTable,
  customStyle,
  onKeyDownHandler,
  paginatedPostRequestBody,
  UTCToDateFormat,
} from "../../../../shared";
import { brandConfigurationActions } from "../../redux/actions";
import { storeActions } from "../../../admin-screens/redux/actions";
import { getConfirmationModal } from "../../../../shared/components/popups/allModals";
import AddBrandConfigurationModal from "./AddBrandConfigurationModal";

const { ALL_BRANDS } = userPermissionsConstants;
const { BRAND_CONFIGURATION_DATA, REFRESH_BRAND_CONFIGURATION } =
  brandConfigurationConstants;
const columnHelper = createColumnHelper();

const BrandConfiguration = (props) => {
  const {
    storesReducer: { allBrandsForFilterDropdown },
    brandConfigurationReducer: {
      brandConfigurationData,
      brandConfigurationPagination,
    },
    getBrandConfiguration,
    getAllBrandForFilterDropdown,
    manageBrandConfiguration,
  } = props;

  //Initialize states
  const [filteredDataValue, setFilteredDataValue] = useState(null);
  const [brandOptionValue, setBrandOptionValue] = useState(null);
  const [searchStr, setSearchStr] = useState(emptyString);
  const [sorting, setSorting] = useState({ id: "brandName", desc: true });
  const [showAddBrandModal, setShowAddBrandModal] = useState(false);
  const [modalBrandSelection, setModalBrandSelection] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalElement, setModalElement] = useState(null);

  const brandFilterOptions = [
    { value: null, name: ALL_BRANDS },
    ...allBrandsForFilterDropdown.map((data) => {
      return { value: data.id, name: data.name };
    }),
  ];

  // Brand options for modal (excludes "All Brands")
  const brandModalOptions = allBrandsForFilterDropdown.map((data) => {
    return { value: data.id, name: data.name };
  });

  // Show loading while a request is running.
  const spinner = useSpinner();

  // set pagination details with request body
  const getPaginatedPostRequestBody = (pageNumber, sortBy) => {
    const filter = createFilter();

    const requestBody = paginatedPostRequestBody(
      brandConfigurationPagination,
      pageNumber,
      searchStr,
      sortBy || sorting,
      filter,
    );
    if (sortBy) {
      setSorting(sortBy);
    }
    return requestBody;
  };

  //Get data
  const getData = (pageNumber, sortBy) => {
    const requestBody = getPaginatedPostRequestBody(pageNumber, sortBy);
    getBrandConfiguration(requestBody);
  };

  const loadNewData = () => {
    getData(0);
  };

  const closeModal = () => setShowModal(false);

  const handleConfirmationModal = (brandId) => {
    setModalElement(
      getConfirmationModal({
        onClose: closeModal,
        Title: brandConfigurationLabels.CONFIRMATION_TITLE,
        message: brandConfigurationLabels.CONFIRMATION_MESSAGE,
        SubmitButtonText: BUTTON_TEXT.CONFIRM,
        CancelButtonText: BUTTON_TEXT.CLOSE,
        onConfirm: () => {
          handleRefreshClick(brandId);
          closeModal();
        },
      }),
    );
    setShowModal(true);
  };

  const userPermissionColumns = useMemo(
    () => [
      columnHelper.accessor("brandName", {
        header: () => brandConfigurationLabels.BRAND_NAME,
      }),
      columnHelper.accessor("appId", {
        enableSorting: false,
        header: () => brandConfigurationLabels.APP_ID,
      }),
      columnHelper.accessor("secret", {
        enableSorting: false,
        header: () => brandConfigurationLabels.SECRET,
      }),
      columnHelper.accessor("platformType", {
        enableSorting: false,
        header: () => brandConfigurationLabels.PLATFORM_TYPE,
      }),
      columnHelper.accessor("updatedAt", {
        header: () => brandConfigurationLabels.LAST_UPDATE,
        cell: ({ row }) => {
          const userData = row.original;
          return userData.updatedAt
            ? UTCToDateFormat(userData.updatedAt)
            : emptyString;
        },
      }),
      columnHelper.accessor("action", {
        header: brandConfigurationLabels.ACTION,
        enableSorting: false,
        size: brandConfigurationLabels.ACTION_COLUMN_SIZE,
        cell: ({ row }) => (
          <FontAwesomeIcon
            title={BUTTON_TEXT.REGENERATE}
            icon={faArrowsRotate}
            role="button"
            data-testid="regenerate-icon"
            className="font-14-px pt-1 me-2 text-center"
            onClick={() => handleConfirmationModal(row.original.brandId)}
          />
        ),
      }),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [handleConfirmationModal]
  );

  const handleRefreshClick = (brandId) => {
    const payload = {
      BrandId: brandId,
      Update: true,
    };
    manageBrandConfiguration(payload, (response) => {
      if (!response) {
        return;
      }
      getData(0);
    });
  };

  //Set filter for the select dropdown
  const setFilteredData = (e) => {
    if (
      e.name !== emptyString &&
      (e.value === emptyString || e.value === null)
    ) {
      setFilteredDataValue(null);
    } else {
      setFilteredDataValue({
        accessType: e.value,
      });
    }
  };

  const setBrandFilterData = (e) => {
    if (
      e.name !== emptyString &&
      (e.value === emptyString || e.value === null)
    ) {
      setBrandOptionValue(null);
    } else {
      setBrandOptionValue(e);
    }
  };

  const createFilter = () => {
    const filters = [];

    if (brandOptionValue?.value) {
      filters.push(`BrandId="${brandOptionValue.value}"`);
    }
    if (filteredDataValue?.accessType) {
      const accessTypeName = Object.values(accessTypesEnums).find(
        (type) => type.id === filteredDataValue.accessType,
      )?.name;

      if (accessTypeName) {
        filters.push(`PlatformType.Trim()="${accessTypeName}"`);
      }
    }

    return filters.length ? `(${filters.join(") AND (")})` : emptyString;
  };

  // Open Add Brand Configuration Modal
  const openAddBrandModal = () => {
    setModalBrandSelection(null);
    setShowAddBrandModal(true);
  };

  // Close Add Brand Configuration Modal
  const closeAddBrandModal = () => {
    setShowAddBrandModal(false);
    setModalBrandSelection(null);
  };

  // Handle modal brand selection
  const handleModalBrandChange = (e) => {
    setModalBrandSelection(e);
  };

  // Handle Generate button in modal
  const handleModalGenerate = () => {
    if (!modalBrandSelection) {
      return;
    }

    const payload = {
      BrandId: modalBrandSelection.value,
      Update: false,
    };

    manageBrandConfiguration(payload, (response) => {
      if (!response) {
        return;
      }
      closeAddBrandModal();
      getData(0);
    });
  };

  //Getting all the users
  useEffect(() => {
    loadNewData();
    // eslint-disable-next-line
  }, [searchStr, brandOptionValue, filteredDataValue]);

  useEffect(() => {
    getAllBrandForFilterDropdown();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {showModal && modalElement}
      <div className="py-3 container-fluid">
        <div className="row align-items-center pb-3">
          <div className="col-md-6 col-lg-3">
            <div className="input-group">
              <span className="input-group-text bg-white text-secondary">
                <i className="fa fa-search" />
              </span>
              <DebounceInput
                value={searchStr}
                placeholder="Search brands"
                type="text"
                className="form-control rounded-end"
                debounceTimeout={brandConfigurationLabels.SEARCH_DEBOUNCE_MS}
                onChange={(e) => setSearchStr(e.target.value)}
              />
              {searchStr?.length > 0 && (
                <span
                  role="button"
                  tabIndex={0}
                  className="user-input-reset"
                  data-testid="resetSearchString"
                  onClick={() => setSearchStr(emptyString)}
                  onKeyDown={(e) => {
                    onKeyDownHandler(e, () => {
                      setSearchStr(emptyString);
                    });
                  }}
                >
                  <i className="fa fa-times" />
                </span>
              )}
            </div>
          </div>
          <div className="col-lg-3 col-md-6 my-1 ">
            <Select
              name="accessType"
              options={accessTypeFilterDropDown}
              getOptionValue={(option) => option.value}
              getOptionLabel={(option) => option.name}
              components={{
                IndicatorSeparator: () => null,
              }}
              defaultValue={accessTypeFilterDropDown[0]}
              isSearchable={true}
              styles={customStyle}
              isMulti={false}
              onChange={(e) => {
                setFilteredData(e);
              }}
            />
          </div>
          <div className="col-md-6 col-lg-3 my-1">
            <Select
              data-testid="brand-base-filter"
              name="brand"
              options={brandFilterOptions}
              getOptionValue={(option) => option.value}
              getOptionLabel={(option) => option.name}
              value={
                brandOptionValue === null
                  ? brandFilterOptions[0]
                  : brandOptionValue
              }
              components={{
                IndicatorSeparator: () => null,
              }}
              isSearchable={true}
              styles={customStyle}
              isMulti={false}
              onChange={(e) => {
                setBrandFilterData(e);
              }}
            />
          </div>
          <div className="col-md-6 col-lg-3 d-flex justify-content-end">
            <button
              type="button"
              className="btn user-permission-button "
              onClick={openAddBrandModal}
              data-testid="add-brand-config-btn"
            >
              <i className="fa fa-plus mx-1" /> Add Config
            </button>
          </div>
        </div>

        <div className="notification-form card shadow">
          <div className="card-body p-0">
            <div>
              {(spinner(BRAND_CONFIGURATION_DATA) ||
                spinner(REFRESH_BRAND_CONFIGURATION)) && <Spinner />}
              <DataTable
                columns={userPermissionColumns}
                data={brandConfigurationData}
                numberOfRowsPerPage={brandConfigurationPagination.size}
                totalRows={brandConfigurationPagination.totalElements}
                pageNumber={brandConfigurationPagination.number + 1}
                onPageChange={(pageNum) => getData(pageNum)}
                onColumnSort={(sortBy) =>
                  getData(brandConfigurationPagination.number, sortBy)
                }
                sortedBy={sorting}
              />
            </div>
          </div>
        </div>
      </div>
      <AddBrandConfigurationModal
        show={showAddBrandModal}
        onClose={closeAddBrandModal}
        brandFilterOptions={brandModalOptions}
        selectedBrand={modalBrandSelection}
        onBrandChange={handleModalBrandChange}
        onGenerate={handleModalGenerate}
        isLoading={spinner(REFRESH_BRAND_CONFIGURATION)}
      />
    </>
  );
};

//Mapping the component's props to the reducer's state
const mapStateToProps = (state) => ({
  storesReducer: state.storesReducer,
  brandConfigurationReducer: state.brandConfigurationReducer,
});

//Mapping the component's props to the related actions
const mapDispatchToProps = (dispatch) => ({
  getAllBrandForFilterDropdown: () =>
    dispatch(storeActions.getAllBrandForFilterDropdown()),
  getBrandConfiguration: (body) =>
    dispatch(brandConfigurationActions.getBrandConfiguration(body)),
  manageBrandConfiguration: (payload, onCallBack) =>
    dispatch(
      brandConfigurationActions.manageBrandConfiguration(payload, onCallBack),
    ),
});

//Mapping action and store the function via props
export default connect(mapStateToProps, mapDispatchToProps)(BrandConfiguration);
